/**
 * Settings → Connect subpage (DEC-060).
 *
 * Live-discovered list of OPBridges + inline pair-approval flow.
 * User does as little manually as possible: the LAN /24 gets scanned on
 * mount, discovered rows appear alongside paired rows, tapping "Connect"
 * on an available row transforms it in-place through:
 *
 *   Available → Waiting for approval → Connected
 *
 * with a countdown pill + subtext "Approve on OPBridge dashboard".
 *
 * Manual URL entry is demoted to a collapsible "Not seeing your
 * OPBridge?" disclosure at the bottom.
 */

import { createSignal, createEffect, For, Show, onCleanup, onMount, batch } from 'solid-js';
import {
  addBridge,
  bridges,
  findBridgeByInstance,
  findBridgeByNode,
  getConnection,
  primaryBridge,
  removeBridge,
  setPrimary,
  updateBridge,
  applyScannedWtAdvert,
  scannedAdvertNewDoors,
  makeAdHocWtConnection,
} from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/store.ts';
import { deviceId, isPeerDeclined } from '../opbridge/storage.ts';
import { isInstalledApp } from '../util/env.ts';
import { expandBridgeCode, extractBridgeUrl, extractWtAdvert, isLanHostLiteral, type ScannedWtAdvert } from '../util/pairingLink.ts';
import { QrScanner } from './QrScanner.tsx';
import { ConfirmChoice } from './ConfirmChoice.tsx';
import {
  guessLanSubnets,
  isOnSuiteDomain,
  mergeDiscovered,
  probeKnownUrls,
  probeSuiteHostedCandidates,
  scanLan,
  walkPeers,
  type DiscoveredBridge,
} from '../opbridge/discovery.ts';
import {
  OpbridgeError,
  OpbridgeHttpClient,
  type PairingRequestStatusResponse,
} from '../opbridge/httpClient.ts';
import { clockSource, clockSourceLabel } from '../opbridge/clockPoll.ts';
import { consumerRevoked, consumerDenied, availableInheritances, inheritedBearerForNode } from '../opbridge/consumerWs.ts';
import { ConnectApprovalModal } from './ConnectApprovalModal.tsx';

type RowState =
  | 'connected'
  | 'available'
  | 'waiting'
  | 'unreachable'
  | 'denied'
  | 'expired';

interface RowView {
  key: string;                 // stable key — bridge_instance_name if known, else `unknown:<url>`
  instance_name: string;       // may be '' for discovered-but-unnamed
  display_name: string;
  /** Operator-set "who runs this bridge" label. Opt-in on OPBridge, never
   *  defaulted — absence is the normal case, render conditionally. */
  owner: string | null;
  bridge_url: string;
  state: RowState;
  paired: boolean;             // true if this row is in bridges() storage
  bridge_id: string | null;    // BridgeConfig.id for paired rows
  // Whether this is the DEFAULT bridge (2026-08-14, supersedes DEC-063's
  // primary-hero semantics) — the bridge new items inherit until pointed
  // elsewhere. No longer a dispatch gate; every paired row is directly and
  // equally reachable. Drives only the small "Default" badge + hiding the
  // redundant "Set as default" action on the row that already is one.
  is_primary: boolean;
  // Peer row: discovered via a paired bridge's /peers walk but NOT yet
  // paired by THIS device (the auto-mint drainer usually catches these
  // shortly after — see inheritanceDrainer.ts). Informational only, no
  // Connect action, until it's actually paired (at which point it moves
  // into the `paired` bucket like any other row).
  is_peer: boolean;
  /** Stable `node_id` when known - from a peer-walk, or resolved off
   *  `GET /health` at Connect time. The join key for redeeming a held
   *  inheritance (DEC-465: never join on `instance_name`). */
  node_id?: string | undefined;
  /** HTTPS form from the mesh, when supplied - the only probe route that
   *  works from the hosted origin. See `PairedBridgeEntry.suite_hostname`. */
  suite_url?: string | undefined;
  /** Peer rows only: this device disconnected it, so auto-adoption is
   *  suppressed by design and the row needs a manual Connect. */
  declined?: boolean;
  /** Unpaired rows only: WHY adoption did not happen, in words the operator
   *  can act on. CP-128 shipped these reasons as `console.info` only, and the
   *  iPad - the one device where auto-adoption is load-bearing - has no
   *  console, so on that device the row simply sat there saying nothing. */
  adopt_reason?: string | undefined;
  countdown_remaining_ms?: number;
  /** Paired rows only: this bridge's `defaultMidiOutput` — the port every
   *  non-device-targeting send resolves to on THIS machine. Per-bridge
   *  because the mesh does not agree on port names: the operator's Mac has
   *  `OPBridge-Ableton` enabled in Live while its legacy `OPButtons` port is
   *  present but disabled for Track and Remote, and the Windows dev PC has
   *  no `OPBridge-Ableton` at all (measured off `GET /midi/ports`,
   *  2026-09-01). A single global constant cannot be right on both. */
  midi_output: string | null;
}

const DEFAULT_CONSUMER = 'opbuttons';
const DEFAULT_BUNDLE = 'opbuttons-full';

/** QR glyph for the pairing CTA. Same idiom as Header.tsx's icons - 20x20
 *  viewBox, `currentColor` so it inherits the button's ink and therefore the
 *  skin. Mixed stroke and fill deliberately: the three finder squares are
 *  stroked so they read at 18px, and the modules are filled because a stroked
 *  2px square at this size is a smudge. */
function IconQr() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <g fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round">
        <rect x="2.4" y="2.4" width="6" height="6" rx="1.2" />
        <rect x="11.6" y="2.4" width="6" height="6" rx="1.2" />
        <rect x="2.4" y="11.6" width="6" height="6" rx="1.2" />
      </g>
      <g fill="currentColor">
        <rect x="4.5" y="4.5" width="1.8" height="1.8" />
        <rect x="13.7" y="4.5" width="1.8" height="1.8" />
        <rect x="4.5" y="13.7" width="1.8" height="1.8" />
        <rect x="11.6" y="11.6" width="2" height="2" />
        <rect x="15.6" y="11.6" width="2" height="2" />
        <rect x="13.6" y="13.6" width="2" height="2" />
        <rect x="11.6" y="15.6" width="2" height="2" />
        <rect x="15.6" y="15.6" width="2" height="2" />
      </g>
    </svg>
  );
}

export function ConnectPage() {
  // ─── Discovery state ─────────────────────────────────────────────
  const [scanRunning, setScanRunning] = createSignal(false);
  const [scanCompleted, setScanCompleted] = createSignal(false);
  const [scanFoundCount, setScanFoundCount] = createSignal(0);
  const [discovered, setDiscovered] = createSignal<DiscoveredBridge[]>([]);

  // ─── Per-row waiting state (in-flight pair requests) ─────────────
  interface PendingPair {
    request_id: string;
    expires_at_ms: number;
    /** Full TTL window in ms (from OPBridge's `expires_in_ms`, 60_000
     *  optimistically before the server responds) - the countdown ring's
     *  denominator, not just the remaining numerator. */
    total_ms: number;
    controller: AbortController;
  }
  const pendingByKey = new Map<string, PendingPair>();
  /** Aborts an in-flight LAN sweep. Held at component scope so `startPair`
   *  can kill a sweep that is already running - see runScan's measurement
   *  note for why a live sweep breaks pairing. */
  let scanAbort: AbortController | null = null;
  const [pendingTick, setPendingTick] = createSignal(0); // forces re-render on countdown

  // ─── "Click on canvas"-style approval PROMPT (2026-08-12 operator ask) ──
  // The row's own inline countdown pill already existed (DEC-060/061) but
  // reads as a subtle sub-text easy to miss in a longer candidate list.
  // This is a louder, dedicated modal for the SAME underlying pairing
  // attempt - no second network path, just a presentation layer that opens
  // when the user taps Connect and closes on resolution or dismissal.
  // Snapshotting the row (not re-deriving it from assembleRows() by key)
  // keeps the modal's copy stable even if discovery re-sorts the list
  // mid-wait.
  const [approvalPromptRow, setApprovalPromptRow] = createSignal<RowView | null>(null);
  const [approvalOutcome, setApprovalOutcome] = createSignal<'waiting' | 'approved' | 'denied' | 'expired' | 'unreachable'>('waiting');
  function closeApprovalPrompt(): void {
    setApprovalPromptRow(null);
  }

  // ─── Manual URL disclosure ───────────────────────────────────────
  const [manualOpen, setManualOpen] = createSignal(false);
  /** In-app camera scanner (2026-08-30). The ONLY zero-typing route into an
   *  installed app on iOS, where a camera-scanned QR always opens Safari. */
  const [scanOpen, setScanOpen] = createSignal(false);
  // Prefill only helps on a LAN-origin (dev / gig laptop) page. On the hosted
  // HTTPS origin a `http://192.168.…` value is actively wrong (mixed content),
  // so start empty there and let the placeholder show the right shape.
  const [manualUrl, setManualUrl] = createSignal(isOnSuiteDomain() ? '' : 'http://192.168.50.');
  const [manualError, setManualError] = createSignal<string | null>(null);
  /** Positive feedback for a QR that armed the offline path. Separate from
   *  `manualError` because it is not a failure, and reusing the error slot
   *  would style a success as a problem. */
  const [scanNotice, setScanNotice] = createSignal<string | null>(null);
  /** A scanned code that would give an already-paired bridge a door on an
   *  address it has never used. Held for the operator's yes (2026-10-06,
   *  OPMod's ask): the bearer rides that door, so a code must never move it
   *  silently - but a venue's DHCP really does move bridges, so it is asked,
   *  not refused. Nothing from the scan runs until this is answered. */
  const [doorMove, setDoorMove] = createSignal<{
    advert: ScannedWtAdvert; bridgeUrl: string; rows: BridgeConfig[];
  } | null>(null);
  /** The WebTransport advert from the most recent scan, held so the PAIRING
   *  handshake itself can ride it.
   *
   *  Pairing at a venue with no internet is the case: a brand-new device has
   *  no stored row, so `wtFor()` has nothing to build a session from, and
   *  `/pairing/*` behind a name that will not resolve is unreachable over
   *  fetch. The scanned QR carries endpoint + pin, which is everything a
   *  session needs - so the handshake can happen with no DNS at all.
   *
   *  Verified reachable over QUIC against the live daemon before this was
   *  built: `GET /pairing/consumers` returns 200 over WebTransport (the
   *  pairing routes are on the same `dp_router` the WT front door serves). */
  const [scannedAdvert, setScannedAdvert] = createSignal<{ advert: ScannedWtAdvert; bridgeUrl: string } | null>(null);

  /** The scanned advert, ONLY for the computer it was scanned for (audit
   *  2026-11-03 B-7). It used to be handed to every pairing client for the
   *  rest of the session, so pairing computer Y after scanning X rode X's door:
   *  X answered Y's handshake and Y's row was stored with X's key. Compared on
   *  the address as `submitManualUrl` makes it. */
  function advertFor(bridgeUrl: string): ScannedWtAdvert | null {
    const s = scannedAdvert();
    if (!s) return null;
    const norm = (u: string) => expandBridgeCode(extractBridgeUrl(u.trim())).replace(/\/+$/, '').toLowerCase();
    return norm(s.bridgeUrl) === norm(bridgeUrl) ? s.advert : null;
  }

  /** A client for the pairing handshake, riding WebTransport when a scanned
   *  advert is held and plain `fetch` otherwise. `makeAdHocWtConnection`
   *  applies the support check and the harness kill switch, so this cannot
   *  silently take pairing off the routing layer the smokes observe. */
  function pairingClient(bridgeUrl: string, timeoutMs: number): OpbridgeHttpClient {
    const advert = advertFor(bridgeUrl);
    const wt = advert ? makeAdHocWtConnection(advert.url, advert.pins) : null;
    return new OpbridgeHttpClient(bridgeUrl, null, timeoutMs, wt);
  }

  // Per-key transient row state overrides (denied/expired states after a
  // failed pair attempt — auto-clear after ~5s). Keyed by row key.
  const [transientState, setTransientState] = createSignal<Map<string, RowState>>(new Map());

  // Bootstrap scan on mount, plus 30s refresh while page is visible.
  let refreshTimer: ReturnType<typeof setInterval> | null = null;
  onMount(() => {
    void runScan();
    refreshTimer = setInterval(() => void runScan(), 30_000);
    void tryConnectFromUrlParam();
  });

  // ─── One-tap connect via `?bridge=<url>` (2026-08-13 operator ask) ──────
  // The manual-URL field proved the real DEC-526 suite-cert URL works with
  // zero cert-trust dance — but typing/pasting a 16-hex-char subdomain on
  // an iPad is real friction. This lets a bookmarked or shared link (e.g.
  // `https://opbuttons.overpoweredapps.net/?bridge=https://<hex>.bridge.
  // overpoweredapps.net:8768`) skip straight to pairing on load. No new
  // OPBridge dependency — reuses the same `submitManualUrl()` path a human
  // would've typed into manually, just pre-filled and auto-submitted.
  // Strips the param immediately (not just on success) so an SPA re-render
  // or a later manual reload doesn't re-trigger it, and it never lingers
  // visibly in the address bar. A saved bookmark is unaffected — the
  // browser keeps its OWN copy of the URL regardless of what happens to
  // the current tab's address bar.
  async function tryConnectFromUrlParam(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('bridge');
    if (!url) return;
    const next = new URL(window.location.href);
    next.searchParams.delete('bridge');
    window.history.replaceState(null, '', next.toString());
    const normalized = url.replace(/\/+$/, '');
    if (bridges().some((b) => b.baseUrl === normalized)) return; // already paired
    setManualUrl(url);
    // NOT a user gesture - see submitManualUrl's gate. This pre-fills and asks;
    // it no longer pairs on its own (security audit F1).
    await submitManualUrl({ confirmedByUser: false });
  }
  // Instant rescan on return-to-tab — the common cert-trust flow (DEC-166
  // Phase 3) is: leave Safari for the iOS Settings app to trust OPBridge's
  // cert, then swipe back. Without this the operator just did the work and
  // still stares at "No OPBridges found" for up to 30s until the interval
  // above happens to fire. runScan() already no-ops while a scan is in
  // flight, so this can't stack with the interval or double-fire.
  function onReturnToTab(): void {
    if (document.visibilityState === 'visible') void runScan();
  }
  document.addEventListener('visibilitychange', onReturnToTab);
  window.addEventListener('focus', onReturnToTab);
  onCleanup(() => {
    if (refreshTimer !== null) clearInterval(refreshTimer);
    document.removeEventListener('visibilitychange', onReturnToTab);
    window.removeEventListener('focus', onReturnToTab);
    for (const p of pendingByKey.values()) p.controller.abort();
    pendingByKey.clear();
  });

  // Countdown ticker — 1 Hz while any pending request is active.
  //
  // REAL BUG, found 2026-08-12 building the approval modal: `pendingByKey`
  // is a plain Map, not a Solid store/signal, so reading `.size` here
  // registers NO reactive dependency. `createEffect` always runs once at
  // mount regardless of dependencies, sees zero pending entries (nothing
  // has started pairing yet), returns early, and - because it tracked
  // nothing - NEVER runs again. The interval was never actually created in
  // the shipped app; the countdown text only ever nudged forward on the
  // handful of explicit `setPendingTick(n => n+1)` calls elsewhere
  // (start / server-response / cancel), not once per second as the
  // comment and DEC-060/061 both describe. Confirmed empirically: sampled
  // the rendered seconds every 600ms across 3s+ with a mocked pending
  // request and it never moved.
  //
  // Fix: make the effect depend on `pendingTick()` itself - already bumped
  // on every `pendingByKey` mutation (set/delete), so this now correctly
  // re-evaluates "is anything pending" whenever the Map changes shape, not
  // just once at mount. The running interval also calls `setPendingTick`,
  // so this effect re-arms a fresh interval every ~1000ms while active -
  // one extra setInterval/clearInterval pair per second, trivial next to
  // a full row-list recompute already happening on the same tick.
  createEffect(() => {
    pendingTick();
    if (pendingByKey.size === 0) return;
    const t = setInterval(() => setPendingTick((n) => n + 1), 1000);
    onCleanup(() => clearInterval(t));
  });

  async function runScan(): Promise<void> {
    if (scanRunning()) return;
    // Never scan while a pairing request is in flight - MEASURED, 2026-08-28.
    //
    // The broadcast scan holds SCAN_CONCURRENCY (32) sockets against dead LAN
    // hosts, and that starves unrelated requests to a real bridge. Properly
    // powered A/B, 160 requests per arm, same machine, same daemon:
    //
    //   scan OFF -> 0/160 over 1s, max 7ms
    //   scan ON  -> 3/160 over 1s, max 18,251ms
    //
    // 18s is a Windows TCP SYN timeout against a dead host. It hit `/health`
    // as well as `/pairing/request`, so this was never the pairing handler.
    // That stall is the whole "Couldn't reach <bridge>" bug: the client
    // budget expires while the daemon sits idle answering curl in ~5ms.
    //
    // The existing "only broadcast when nothing is paired" guard below does
    // NOT cover this, and the gap is precisely the case that matters: after
    // disconnecting every bridge there is nothing to peer-walk, so the full
    // sweep runs at exactly the moment the operator is trying to pair.
    if (pendingByKey.size > 0) return;
    setScanRunning(true);
    try {
      // Prefer the authoritative peer walk over broadcast scanning: if any
      // bridge is already paired, we can ask it for the mesh membership
      // directly. That avoids 254 parallel `/health` probes to random LAN
      // hosts (which saturate the browser's fetch queue, block the
      // paired-connection's health tick, and burn a wall of CORS-blocked
      // console errors). Broadcast scan only runs when we have zero
      // paired bridges to bootstrap from.
      const peerWalks: DiscoveredBridge[][] = [];
      let anyPairedAndConnected = false;
      for (const cfg of bridges()) {
        const conn = getConnection(cfg.id);
        if (!conn || conn.status() !== 'connected') continue;
        anyPairedAndConnected = true;
        const walk = await walkPeers(conn.httpClient());
        peerWalks.push(walk);
      }
      let scanned: DiscoveredBridge[] = [];

      // Before broadcasting at 254 hosts, ask the ones whose address we
      // already know (2026-08-28). `anyPairedAndConnected` is false whenever a
      // paired bridge is merely OFFLINE - a daemon restart, a Wi-Fi blip, a
      // sleeping laptop - and the old code swept the whole subnet anyway, even
      // though `cfg.baseUrl` was sitting right there in storage.
      //
      // That was the worst possible response to a transient outage: the sweep
      // holds 32 sockets against dead hosts and starves the very reconnect it
      // is trying to achieve (measured: a live sweep turns a ~5ms request into
      // an 18.2s one). A bridge coming back would fight a socket storm we
      // started on its behalf, which is the likely mechanism behind the
      // "connected, disconnected, connected again" flapping on reconnect.
      //
      // N direct probes where N is the number of paired bridges - usually one
      // or two - instead of 254. The broadcast sweep is now what it always
      // should have been: the BOOTSTRAP path for a device that has never
      // paired with anything and genuinely has no address to try.
      if (!anyPairedAndConnected) {
        const knownUrls = bridges().map((b) => b.baseUrl).filter(Boolean);
        if (knownUrls.length > 0) {
          scanned = await probeKnownUrls(knownUrls);
          for (const hit of scanned) {
            setDiscovered((prev) =>
              prev.some((d) => d.instance_name === hit.instance_name) ? prev : [...prev, hit],
            );
          }
        }
      }

      if (!anyPairedAndConnected && scanned.length === 0) {
        if (isOnSuiteDomain()) {
          // Served from opbuttons.overpoweredapps.net (DEC-166 Phase 3): a
          // broadcast scan's plain `http://<lan-ip>` probes are mixed-content
          // blocked wholesale from an https:// page, not just individually
          // unreachable — scanLan() would burn ~762 guaranteed-fail requests
          // for nothing. Three fixed, ordered candidates instead (same-machine
          // loopback, then opbridge.local for a different LAN device).
          const hit = await probeSuiteHostedCandidates();
          if (hit) scanned = [hit];
        } else {
          // Surface each bridge the instant it answers `/health` instead of
          // waiting out the full ~762-host sweep. A bridge on this subnet shows
          // in well under a second rather than ~6s (the "Connect takes a while"
          // complaint). The final merge below reconciles the authoritative set.
          // Pass a real signal so a pair started MID-SWEEP kills the sweep
          // immediately. The `pendingByKey` guard above only stops a scan
          // from STARTING; without this, a scan already running when the
          // operator taps Connect keeps its 32 dead sockets open and stalls
          // the very request it is competing with.
          scanAbort?.abort();
          scanAbort = new AbortController();
          scanned = await scanLan(guessLanSubnets(), scanAbort.signal, (hit) => {
            setDiscovered((prev) =>
              prev.some((d) => d.instance_name === hit.instance_name) ? prev : [...prev, hit],
            );
          });
        }
      }
      const merged = mergeDiscovered(scanned, ...peerWalks);
      setDiscovered(merged);
      setScanFoundCount(merged.length);
      setScanCompleted(true);
    } finally {
      setScanRunning(false);
    }
  }

  // ─── Row list assembly (2026-08-14 equal-peer shape, supersedes
  // DEC-063's primary-hero/peer split) ───────────────────────────────
  // Three row buckets emerge from bridges() + discovered():
  //   1. Paired — EVERY row in bridges() storage, flat and equal. One of
  //      them is the default (is_primary); that's a badge, not a
  //      dispatch gate — every row here already carries its own live,
  //      directly-reachable connection (store.ts's reconcile()).
  //   2. Peers — discovered via a paired bridge's /peers walk but NOT
  //      yet paired by this device (the auto-mint drainer usually
  //      catches these shortly). Informational only, no Connect action.
  //   3. Candidates — LAN-scan results not yet paired and not peers.
  //
  // Every row still carries is_primary + is_peer so the render can group
  // them without reassembling from the source lists.

  function computeState(status: string, failures: number, everConnected: boolean): RowState {
    // Confirmed-offline threshold: 2 consecutive /health misses. Since
    // 2026-09-30 a CONNECTED bridge's status itself rides out two misses and
    // flips on the third (connection.ts `UNREACHABLE_AFTER_MISSES`), so this
    // threshold only still matters for a bridge that never connected.
    const CONFIRMED_OFFLINE_THRESHOLD = 2;
    if (status === 'unauthorized') return 'denied';
    if (status === 'connected') return 'connected';
    if (failures >= CONFIRMED_OFFLINE_THRESHOLD) return 'unreachable';
    if (everConnected) return 'connected';
    return 'unreachable';
  }

  function assembleRows(): { paired: RowView[]; peers: RowView[]; candidates: RowView[] } {
    // See the historical bug note this replaced: assembleRows() must read
    // pendingTick() (a plain Map's mutations don't register as a Solid
    // dependency on their own) so every call site re-renders when a pair
    // request starts/resolves, not just when some unrelated signal
    // happens to change nearby.
    void pendingTick();
    // Read as a tracked dependency, so a reason recorded by the async adopt
    // path re-renders the rows. Attached to UNPAIRED rows only: a paired row
    // is built from `bridges()` and cannot carry a stale "why it didn't
    // adopt", because it did.
    const reasons = adoptReasons();

    const paired: RowView[] = [];
    const knownInstances = new Set<string>();
    for (const cfg of bridges()) {
      if (cfg.bridge_instance_name) knownInstances.add(cfg.bridge_instance_name);
      const conn = getConnection(cfg.id);
      const status = conn ? conn.statusSignal()() : 'connecting';
      const failures = conn ? conn.consecutiveFailuresSignal()() : 0;
      const key = cfg.bridge_instance_name || `id:${cfg.id}`;
      const pending = pendingByKey.get(key);
      const trans = transientState().get(key);
      const row: RowView = {
        key,
        instance_name: cfg.bridge_instance_name,
        display_name: cfg.name || cfg.bridge_instance_name || cfg.baseUrl,
        owner: conn?.health()?.owner ?? null,
        bridge_url: cfg.baseUrl,
        state: pending ? 'waiting' : (trans ?? computeState(status, failures, cfg.everConnected)),
        paired: true,
        bridge_id: cfg.id,
        is_primary: cfg.is_primary,
        is_peer: false,
        midi_output: cfg.defaultMidiOutput ?? null,
      };
      if (pending) row.countdown_remaining_ms = Math.max(0, pending.expires_at_ms - Date.now());
      paired.push(row);
    }
    // Default row first — not a hero, just a stable, familiar reading
    // order (the row most items silently target sits at the top).
    paired.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));

    const peers: RowView[] = [];
    const candidates: RowView[] = [];

    for (const d of discovered()) {
      const key = d.instance_name || `url:${d.bridge_url}`;
      // Already paired (shown above) — skip, would dupe.
      if (d.instance_name && knownInstances.has(d.instance_name)) continue;

      if (d.source === 'peer-walk') {
        // Seen via mesh membership, not yet paired by this device. No
        // direct reachability signal for an unpaired row — informational
        // only, no Connect action (the auto-mint drainer is the real
        // path that turns this into a `paired` row automatically).
        peers.push({
          key,
          instance_name: d.instance_name,
          display_name: d.display_name,
          owner: d.owner,
          bridge_url: d.bridge_url,
          state: 'unreachable',
          paired: false,
          bridge_id: null,
          is_primary: false,
          is_peer: true,
          midi_output: null,
          // A peer this device explicitly DISCONNECTED is on the declined
          // list (store.ts's removeBridge -> declinePeer), and
          // inheritanceDrainer skips it forever until an explicit add calls
          // undeclinePeer. Without this flag the row kept promising "pairs
          // automatically" for something that never would, AND offered no
          // action - the operator could neither wait for it nor fix it.
          declined: !!d.node_id && isPeerDeclined(d.node_id),
          node_id: d.node_id,
          suite_url: d.suite_url,
          adopt_reason: reasons.get(key),
        });
        continue;
      }

      const pending = pendingByKey.get(key);
      let state: RowState;
      if (pending) {
        state = 'waiting';
      } else {
        const trans = transientState().get(key);
        state = trans ?? (d.reachable ? 'available' : 'unreachable');
      }
      const row: RowView = {
        key,
        instance_name: d.instance_name,
        display_name: d.display_name,
        owner: d.owner,
        bridge_url: d.bridge_url,
        state,
        paired: false,
        bridge_id: null,
        is_primary: false,
        is_peer: false,
        midi_output: null,
        node_id: d.node_id,
        suite_url: d.suite_url,
        adopt_reason: reasons.get(key),
      };
      if (pending) {
        row.countdown_remaining_ms = Math.max(0, pending.expires_at_ms - Date.now());
      }
      candidates.push(row);
    }

    return { paired, peers, candidates };
  }

  /** Row key -> why adoption declined, for the row itself to say out loud.
   *
   *  The Map's IDENTITY is replaced on every write, never mutated in place.
   *  A plain-Map read registers no Solid dependency, and that is not a
   *  theoretical worry in this file: it is the exact trap that froze the
   *  header pill at "OPBridge CONNECTED" through a 147-second outage and got
   *  a gig force-quit (CP-132).
   *
   *  Keyed on the ROW key, which is not always the node id - see the auto
   *  effect below, where the two deliberately differ. */
  const [adoptReasons, setAdoptReasons] = createSignal<Map<string, string>>(new Map());

  /** Record why `row` was not adopted, and return false so the call sites
   *  stay one-liners.
   *
   *  The console line STAYS, and deliberately keeps the node ids and URLs: a
   *  developer reading a browser console needs those and cannot use plain
   *  English, while an operator reading a Connect row is the exact reverse.
   *  Two audiences, two sentences, rather than one that serves neither. */
  function noAdopt(row: RowView, consoleDetail: string, operatorReason: string): false {
    console.info(`[pairing] no adopt for "${row.display_name}": ${consoleDetail}`);
    setAdoptReasons((prev) => {
      const next = new Map(prev);
      next.set(row.key, operatorReason);
      return next;
    });
    return false;
  }

  /** Drop a stale reason once the row is genuinely usable. Without this, a row
   *  that failed once, was then adopted, and was later disconnected would
   *  resurface its OLD explanation as though it were current. */
  function clearAdoptReason(key: string): void {
    setAdoptReasons((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }

  // Force re-computation when scan / countdown / bridge list changes.
  const [rowVersion, setRowVersion] = createSignal(0);
  createEffect(() => {
    // Read reactive sources so effect re-runs on changes.
    void bridges();
    void discovered();
    void pendingTick();
    void transientState();
    setRowVersion((n) => n + 1);
  });

  // ─── Pairing flow ────────────────────────────────────────────────

  /** Redeem an inheritance this device ALREADY HOLDS for `row`, instead of
   *  running a second pairing ceremony against a bridge the mesh has already
   *  credentialed us for. Returns true when the row was adopted.
   *
   *  THE BUG THIS FIXES, operator 2026-09-07, verbatim: *"i click connect to
   *  the 2nd mac pc and it sends a prompt to it. that's not what the fan out
   *  auto pair is supposed to do. both should say connected when i connect to
   *  just 1 opbridge."* They were right, and fan-out was never the broken
   *  half - OPBridge measured the bearer minted on BOTH bridges in the same
   *  second from one ceremony. What was missing was any code that SPENT it:
   *  `inheritedBearerForNode` was exported with ZERO call sites, so Connect
   *  fell through to a full ceremony and the second bridge prompted. The
   *  banner above has promised "connects without a full ceremony" the whole
   *  time, which was simply not implemented.
   *
   *  WHY THIS IS NOT THE AUTO-PAIRING THE OPERATOR REJECTED on 2026-08-30,
   *  and the distinction is the whole of DEC-200. `autoAdd` stays false: no
   *  bridge is ever added that the operator did not click. Here they clicked.
   *  The two channels stay separate exactly as DEC-200 requires - the ADDRESS
   *  comes from mDNS discovery plus that deliberate gesture, never from a peer
   *  list (security audit F2), and only the CREDENTIAL comes from
   *  `/pairing/inheritances`, keyed on the stable `node_id` (DEC-465), never
   *  on the mDNS label.
   *
   *  Falls back to the full ceremony whenever anything is unknown: no node_id
   *  resolvable, or no inheritance held for it. */
  async function adoptInheritedBearer(row: RowView): Promise<boolean> {
    // ⚠️ ALWAYS PROBE, even when the row already carries a node_id. This is
    // the whole trust argument, and an earlier cut got it wrong twice.
    //
    // A peer-walk row's address and node_id are BOTH just claims from another
    // bridge's unauthenticated `/peers/paired-bridges`. Trusting the row's own
    // `node_id` would mean a peer could name any address and any identity and
    // have us spend a real bearer at it. So we ask the address itself who it
    // is: `GET /health` is unauthenticated, costs one request, and its answer
    // is the bridge's OWN claim about its identity rather than a third party's.
    //
    // The address is therefore verified by US (it answered, and it answered as
    // an OPBridge), and the credential is spent only if the identity it
    // reports matches one we already hold a bearer for. A lying peer list gets
    // you nothing: point us at a host we hold no inheritance for and adoption
    // simply declines.
    //
    // This also replaces the `source === 'lan-scan'` / `reachable` guards that
    // preceded it, BOTH of which failed on the operator's real rig: when a
    // bridge is already paired and connected, `runScan` skips every probe path
    // (they are all gated on `!anyPairedAndConnected`), so a mesh peer arrives
    // peer-walk-only with `reachable: false` and was never adoptable.
    // Probe the HTTPS form FIRST when the mesh gave us one. On the hosted
    // origin (the iPad) the plain `http://<lan-ip>` form is mixed-content
    // blocked outright, so without this there is no reachable probe at all and
    // a mesh peer can never be adopted there - the operator's actual report,
    // 2026-09-07: "i'm pairing to the dev pc but it doesnt auto connect to the
    // mac". The suite host answers 200 with a valid cert (measured); the
    // derivable `https://<lan-ip>:8768` does NOT, because the leaf does not
    // cover a bare IP, so it is deliberately not attempted.
    const probeUrls = [row.suite_url, row.bridge_url].filter((u): u is string => !!u);
    let nodeId = '';
    let probed = '';
    for (const candidate of probeUrls) {
      try {
        const h = await pairingClient(candidate, 3000).health();
        if (!h.ok || h.app !== 'OPBridge') continue;
        nodeId = (h as { node_id?: string }).node_id ?? '';
        probed = candidate;
        break;
      } catch { /* try the next form */ }
    }
    if (!probed) {
      return noAdopt(
        row,
        `no reachable /health (tried ${probeUrls.join(', ') || 'nothing'})`,
        // No "tap Connect" here, deliberately. `stateLabel()` says that one
        // line above and the button is on the same row, so repeating it made
        // the row read as two instructions rather than one fact plus one
        // action. Seen in the 2026-09-14 screenshot, not reasoned about.
        'Could not reach this machine on the network, so nothing was sent to it.',
      );
    }
    if (!nodeId) {
      return noAdopt(
        row,
        'bridge reports no node_id',
        'It answered, but did not say which machine it is, so no saved credential '
        + 'could be matched to it.',
      );
    }
    // A peer list that named a different machine than the one answering is
    // exactly the case this catches - spend nothing on it.
    if (row.node_id && row.node_id !== nodeId) {
      return noAdopt(
        row,
        `the peer list claimed node ${row.node_id} but that address reports ${nodeId}`,
        'Another OPBridge said this machine was at this address, but the address '
        + 'answered as a different one. Nothing was sent to it.',
      );
    }

    const inh = inheritedBearerForNode(nodeId);
    if (!inh || !inh.bearer) {
      return noAdopt(
        row,
        `(node ${nodeId}) no inheritance held (registry holds ${availableInheritances().length})`,
        'No saved credential for this machine yet, so it cannot connect on its own.',
      );
    }
    // An explicitly disconnected peer must STAY gone until an explicit add.
    // Same rule inheritanceDrainer applies; without it the auto-adopt effect
    // would resurrect a row the operator deliberately removed.
    if (isPeerDeclined(nodeId)) {
      console.info(`[pairing] no adopt for "${row.display_name}": peer was explicitly disconnected`);
      return false;
    }

    // ⚠️ DEDUPE, and this bug was REAL, not theoretical - it shipped in the
    // first cut of auto-adopt and produced TWO rows for one bridge, which the
    // operator would have seen as the Mac listed twice.
    //
    // Two independent callers race here: the auto-adopt effect and the click
    // path (`startPair`). Both await `/health` first, so both can pass every
    // check above before either writes, and `addBridge` is a read-modify-write
    // on the stored list - so the second write recreates the row rather than
    // updating it. Caught only because the smoke printed row NAMES instead of
    // a count; `rows=3` on its own looked unremarkable.
    //
    // Two guards, because they cover different halves: the in-flight set stops
    // two concurrent calls, and the stored-row check stops a later call for a
    // bridge already adopted. Returning TRUE (not false) is deliberate - the
    // row IS adopted, so `startPair` must short-circuit rather than fall
    // through and run the ceremony this whole function exists to avoid.
    if (bridges().some((b) => b.nodeId === nodeId)) {
      console.info(`[pairing] "${row.display_name}" already adopted (node ${nodeId}) - no ceremony`);
      clearAdoptReason(row.key);
      return true;
    }
    if (adoptInFlight.has(nodeId)) {
      console.info(`[pairing] adopt already in flight for node ${nodeId} - not duplicating`);
      return true;
    }
    adoptInFlight.add(nodeId);

    addBridge({
      name: row.display_name,
      baseUrl: probed,     // the form we actually reached, not the one we could not
      bearer: inh.bearer,
      bridge_instance_name: row.instance_name || inh.bridge_instance_name,
      nodeId,
      is_primary: false,
      scope_bundle_id: inh.scope_bundle_id,
      scope_bundle_version: inh.scope_bundle_version,
    });
    console.info(
      `[pairing] adopted a held inheritance for "${row.display_name}" (node ${nodeId}) - ` +
      'no second ceremony fired',
    );
    clearAdoptReason(row.key);
    return true;
  }

  /** Bridges this effect has already tried, so a failed attempt cannot spin.
   *  Keyed on node_id where known, else the row key. */
  const autoAdoptTried = new Set<string>();

  /** node_ids with an adoption mid-flight, so the auto effect and a click
   *  cannot both write a row for the same bridge. Never cleared: an adopted
   *  bridge stays adopted for the life of the page, and a rebind reloads. */
  const adoptInFlight = new Set<string>();

  /* AUTO-ADOPT, operator 2026-09-07: *"both should say connected when i
   * connect to just 1 opbridge. doesn't matter which one."* One ceremony
   * credentials the whole mesh, so a second machine should not need a second
   * gesture - the click this replaces was a half-fix against that sentence.
   *
   * ⚠️ THE GUARD IS THE WHOLE DESIGN, and it is narrower than "adopt anything
   * we hold a bearer for". BOTH must hold:
   *
   *   1. OUR OWN PROBE REACHED THE ADDRESS, `reachable === true`.
   *
   *      ⚠️ THIS WAS `source === 'lan-scan'` AND THAT WAS WRONG ON A REAL
   *      RIG - operator, 2026-09-07: "still not auto connecting to other pcs
   *      on mesh", while a fake-bridge smoke passed. `mergeDiscovered`'s
   *      precedence is `peer-walk > lan-scan`, so the moment a primary is
   *      paired and `walkPeers` runs, a bridge the LAN scan ALSO found merges
   *      as `peer-walk` and the source test skipped it. The smoke never saw
   *      it because with no paired primary there is no peer-walk at all.
   *
   *      `reachable` is the honest test, and it is strictly stronger than the
   *      source label: `walkPeers` hardcodes `reachable: false` ("a mesh-
   *      membership statement, not a liveness statement"), `probeHealth` sets
   *      `true` only on a valid `/health` from that address, and merge ORs the
   *      bit across sources. So `reachable === true` MEANS WE PROBED IT AND IT
   *      ANSWERED - an address verified by us, never one taken on a peer's
   *      word. A peer-walk row nobody has reached stays `false` and is still
   *      skipped, which is the DEC-200 / audit-F2 property that mattered.
   *   2. WE ALREADY HOLD A CREDENTIAL MINTED FOR IT, keyed on the stable
   *      `node_id` (DEC-465). The bearer's existence is the proof that the
   *      operator's OWN pairing ceremony authorised this bridge - nothing is
   *      being trusted on a stranger's say-so.
   *
   * So this is not the auto-pairing rejected on 2026-08-30: that was adding
   * bridges the operator never paired, on an address supplied by someone
   * else. Here the operator paired, and this device found the machine itself.
   *
   * An explicitly disconnected peer stays gone - `isPeerDeclined` is checked
   * exactly as `inheritanceDrainer` does, or this would resurrect a row the
   * operator removed on purpose (pinned by
   * `inheritance_respects_explicit_disconnect`). */
  createEffect(() => {
    const inheritances = availableInheritances();
    const rows = discovered();
    if (inheritances.length === 0) return;
    // Harness kill switch, same shape as projectSync / matrixSync / rigStateSync
    // in `opbuttons-bot`'s client.ts. Auto-adopt reacts to the REAL mesh, so a
    // smoke with a seeded bridge silently adopts whatever machines are on the
    // operator's LAN - `connect_page_confirmed_offline_within_5s` started
    // failing the moment this shipped, because it adopted the real Mac Studio
    // mid-run. An automated context and a freshly-wiped iPad look identical to
    // the app; only the harness knows the difference, so the harness declares
    // it. Default in the app is ON - absence of the key means enabled.
    try {
      if (localStorage.getItem('opbuttons.autoAdopt.disabled') === '1') return;
    } catch { /* private mode - treat as enabled */ }
    for (const d of rows) {
      // Cheap pre-filter so this does not probe every row on every scan: if
      // the row advertises a node_id we hold NO inheritance for, there is
      // nothing to spend and no reason to ask. A row with no node_id (an
      // mDNS hit) still gets probed, because that is how its identity is
      // learned in the first place.
      if (d.node_id && !inheritedBearerForNode(d.node_id)) continue;
      if (d.instance_name && findBridgeByInstance(d.instance_name)) continue;  // already paired
      // TWO DIFFERENT KEYS, on purpose, and conflating them is a real bug.
      //
      // `triedKey` is the DEDUPE key and must be the strongest identity we
      // have, so node_id leads: two discovery sources naming one machine must
      // not both get probed.
      //
      // `rowKey` is the RENDER key and must match what `assembleRows` builds
      // for a discovered row (`instance_name || url:`), or a decline reason
      // recorded here would be filed under a key no row ever looks up - and
      // the note would be silently invisible, which is the failure this whole
      // change exists to remove.
      const triedKey = d.node_id || `url:${d.bridge_url}`;
      const rowKey = d.instance_name || `url:${d.bridge_url}`;
      if (autoAdoptTried.has(triedKey)) continue;
      autoAdoptTried.add(triedKey);
      void (async () => {
        const adopted = await adoptInheritedBearer({
          key: rowKey,
          instance_name: d.instance_name,
          display_name: d.display_name,
          owner: d.owner,
          bridge_url: d.bridge_url,
          state: 'available',
          paired: false,
          bridge_id: null,
          is_primary: false,
          is_peer: false,
          midi_output: null,
          node_id: d.node_id,
          // ⚠️ MUST be passed. Without it the AUTO path probes only the plain
          // `http://<lan-ip>` form, which a hosted-origin device (the iPad)
          // cannot reach - while the CLICK path worked, because `assembleRows`
          // attaches it. That asymmetry was exactly the operator's report on
          // 2026-09-08: "it seems to be connecting to both but i manually have
          // to click connect on the other mesh pc".
          suite_url: d.suite_url,
        });
        if (adopted) {
          console.info(`[pairing] auto-adopted "${d.display_name}" - one ceremony, whole mesh`);
        }
      })();
    }
  });

  async function startPair(row: RowView): Promise<void> {
    if (pendingByKey.has(row.key)) return;
    // Spend a credential the mesh already gave us before asking for another.
    // Deliberately FIRST: everything below this point prompts a human on the
    // far bridge, which is precisely the behaviour being fixed.
    if (await adoptInheritedBearer(row)) return;
    // Kill any in-flight LAN sweep BEFORE issuing the request. Measured: a
    // live sweep turns a ~3ms pairing POST into an 18s stall (see runScan).
    // This is the single highest-value line in the pairing path - without it
    // the first tap after disconnecting everything races 32 dead sockets.
    scanAbort?.abort();
    scanAbort = null;
    // 15s, raised from 4s on 2026-08-28. Both numbers matter, so both are
    // recorded rather than just the new one:
    //
    //   - the bridge answers this route in ~5ms (measured, 3 runs each on two
    //     machines), and 71ms was the worst observed in a real browser with 32
    //     requests already in flight;
    //   - the request's own server-side TTL is 60s.
    //
    // So 4s was neither derived from the operation's cost nor from its
    // deadline - it was the client default - and it sat only ~56x above the
    // worst real measurement while the operator was hitting AbortError
    // repeatedly. 15s keeps ~200x headroom over the measured worst case and
    // still fails well inside the 60s TTL, so a genuinely dead bridge is still
    // reported promptly rather than hanging the modal.
    //
    // ⚠️ This is not a proven fix for the AbortError - the trigger is still
    // unidentified, and a LAN-scan-contention theory was tested and did NOT
    // hold. It removes one whole failure class (a budget expiring on a
    // user-initiated one-shot) rather than claiming to have found the cause.
    const client = pairingClient(row.bridge_url, 15_000);
    const controller = new AbortController();
    // Optimistic UI: create pending record before the request so the row
    // flips to Waiting immediately.
    const provisional: PendingPair = {
      request_id: '',
      expires_at_ms: Date.now() + 60_000,
      total_ms: 60_000,
      controller,
    };
    pendingByKey.set(row.key, provisional);
    setPendingTick((n) => n + 1);
    setApprovalOutcome('waiting');
    setApprovalPromptRow(row);

    // ─── Why the request and the POLL have separate catches (2026-08-28) ───
    //
    // They used to share one, which told the operator two different lies.
    //
    // 1. A THROWN request rendered "Not approved - denied or could not be
    //    sent". Operator-denied and never-sent are opposite diagnoses (one
    //    means "you refused this", the other "the bridge never heard you"),
    //    and OPBridge cannot even produce the first without a human clicking
    //    Deny - `pairing_coordinator::deny_request` has no auto-deny path. So
    //    every network-side failure was reported as a human decision.
    // 2. `pollUntilResolved` calls `applyApproval` INSIDE this try, so a throw
    //    AFTER a successful approval also said "Not approved" - for a pairing
    //    that had actually succeeded.
    //
    // Found by reproducing the operator's "Not approved" through the real UI
    // and logging what the page really got: `AbortError: signal is aborted
    // without reason` on the POST, while the same endpoint answers in ~5ms to
    // curl and returns 201. It is INTERMITTENT and the trigger is not yet
    // proven - a LAN-scan contention theory was tested and did NOT hold
    // (clicking mid-scan paired fine). That is exactly why this needs to
    // report what happened instead of guessing: the next occurrence should
    // name itself rather than hide behind "denied".
    // ONE automatic retry, and only for an abort/network throw - never for a
    // response the bridge actually gave us.
    //
    // Safe to repeat specifically because of DEC-468: the request carries this
    // device's own `device_id`, and OPBridge REPLACES that device's prior
    // token rather than stacking a second one. So a retry cannot leave a
    // duplicate pending request or an orphaned bearer - which is exactly why
    // this is a retry and not a "press Try again yourself" prompt.
    //
    // ⚠️ MEASURED, and it does NOT rescue the case it was written for. With
    // the first attempt forced to fail, the retry fires (both warnings appear)
    // and then hits a REAL `timeout after 15000ms` against a bridge that
    // answers curl in ~5ms. So failures to one origin are CORRELATED, not
    // independent: once a request fails, the next one hangs too, which is
    // exactly the operator's "Try again keeps failing" report.
    //
    // Kept because it is free and strictly better than nothing on a genuinely
    // one-off blip, but the real fix is upstream of here and not yet found -
    // do not read this retry as the cure, and do not delete the honest
    // `unreachable` outcome on the strength of it.
    const openRequest = () => client.pairingRequest({
      consumer: DEFAULT_CONSUMER,
      fan_out: true,
      requested_bundle_id: DEFAULT_BUNDLE,
      device_id: deviceId(), // DEC-468: replace this device's own prior token, don't stack
    });

    let opened;
    try {
      opened = await openRequest();
    } catch (first) {
      console.warn(`[pairing] request to ${row.display_name} (${row.bridge_url}) threw; retrying once`, first);
      try {
        opened = await openRequest();
      } catch (err) {
        pendingByKey.delete(row.key);
        markTransient(row.key, 'unreachable', 5000);
        if (approvalPromptRow()?.key === row.key) setApprovalOutcome('unreachable');
        console.warn(`[pairing] retry to ${row.display_name} (${row.bridge_url}) also failed`, err);
        return;
      }
    }

    provisional.request_id = opened.request_id;
    provisional.expires_at_ms = Date.now() + opened.expires_in_ms;
    provisional.total_ms = opened.expires_in_ms;
    setPendingTick((n) => n + 1);

    try {
      await pollUntilResolved(row, client, opened.request_id, opened.long_poll_max_ms);
    } catch (err) {
      // The request WAS accepted, so this is not a "could not be sent" case
      // and must never claim the operator denied it.
      pendingByKey.delete(row.key);
      markTransient(row.key, 'unreachable', 5000);
      if (approvalPromptRow()?.key === row.key) setApprovalOutcome('unreachable');
      console.warn(`[pairing] request ${opened.request_id} to ${row.display_name} was accepted but resolving it failed`, err);
    }
  }

  async function pollUntilResolved(
    row: RowView,
    client: OpbridgeHttpClient,
    requestId: string,
    longPollMs: number,
  ): Promise<void> {
    const timeoutMs = Math.min(longPollMs, 8000);
    while (true) {
      const pending = pendingByKey.get(row.key);
      if (!pending || pending.controller.signal.aborted) return;
      if (Date.now() >= pending.expires_at_ms) {
        pendingByKey.delete(row.key);
        markTransient(row.key, 'expired', 5000);
        if (approvalPromptRow()?.key === row.key) setApprovalOutcome('expired');
        return;
      }
      let resp: PairingRequestStatusResponse;
      try {
        resp = await client.pairingRequestPoll(requestId, timeoutMs);
      } catch (_err) {
        // Transient network hiccup — brief backoff then continue while
        // the request TTL still has room.
        await sleep(1000);
        continue;
      }
      if (resp.status === 'pending') {
        // Nothing to do — loop and re-poll.
        continue;
      }
      pendingByKey.delete(row.key);
      if (resp.status === 'approved') {
        const same = resp.local_bearer ? await provenSameRow(row, resp, client) : null;
        applyApproval(row, resp, same);
        void runScan();
        if (approvalPromptRow()?.key === row.key) {
          setApprovalOutcome('approved');
          setTimeout(() => { if (approvalPromptRow()?.key === row.key) closeApprovalPrompt(); }, 1400);
        }
        return;
      }
      if (resp.status === 'denied') {
        markTransient(row.key, 'denied', 5000);
        if (approvalPromptRow()?.key === row.key) setApprovalOutcome('denied');
        return;
      }
      // expired
      markTransient(row.key, 'expired', 5000);
      if (approvalPromptRow()?.key === row.key) setApprovalOutcome('expired');
      return;
    }
  }

  /** The rest of a QR scan, once any door move has been answered: arm the
   *  offline path FIRST, before any network call. This is the break-glass
   *  case: a device whose cached pin died while it was away, now at a venue
   *  with no internet. `submitManualUrl` probes /health, which cannot succeed
   *  there - so if the advert were applied after it, the rescue would never
   *  run. Applying it first also means the probe itself can ride
   *  WebTransport. `movedIds` are rows the operator agreed may take the
   *  code's new door. */
  function continueScan(
    bridgeUrl: string, advert: ScannedWtAdvert | null, movedIds: string[], refusedNotice: string | null = null,
  ): void {
    let notice = refusedNotice;
    // A scan with no advert (or "keep the current address") must not leave an
    // earlier scan's door in play for this one (B-7).
    setScannedAdvert(advert ? { advert, bridgeUrl } : null);
    if (advert) {
      // Held for the pairing handshake below, which on a brand-new device is
      // the ONLY way it reaches the daemon offline.
      // Name the target from the SAME link (security audit F5). The link
      // carries the bridge's own URL beside the advert, so this never has to
      // guess - and guessing was the defect: with no target this used to fall
      // back to the PRIMARY row and re-point a working bridge's offline
      // transport at the scanned code.
      let armed = applyScannedWtAdvert(advert, { linkBridgeUrl: bridgeUrl });
      for (const id of movedIds) armed += applyScannedWtAdvert(advert, { bridgeId: id });
      if (armed > 0) {
        // Not an error - staying silent about a successful rescue would leave
        // the operator unsure whether to scan again.
        notice = `Offline path armed from the code (${armed === 1 ? '1 bridge' : `${armed} bridges`}).`;
      }
    }
    setManualUrl(bridgeUrl);
    void submitManualUrl({ confirmedByUser: true }); // the operator pointed a camera at it
    // AFTER the submit: it clears the notice synchronously before its first
    // await, which erased this one the instant it was set (found 2026-10-06;
    // the "armed" notice had never been visible after a scan).
    if (notice) setScanNotice(notice);
  }

  /** Carry a scanned advert onto the row a pair just created or refreshed.
   *
   *  By explicit id, never by host-match: the row stores the SUITE HOSTNAME
   *  as its `baseUrl` while the advert is a LAN IP, so a host-match would miss
   *  it and fall back to the primary - arming a DIFFERENT, already-working
   *  bridge with this one's pin. Silent corruption on any second pairing.
   *
   *  Without this the device would pair fine offline and then have no offline
   *  path afterwards, which is the whole point of having paired that way. */
  function armPairedRowFromScan(bridgeId: string | null | undefined, bridgeUrl: string): void {
    const advert = advertFor(bridgeUrl);
    if (!advert || !bridgeId) return;
    applyScannedWtAdvert(advert, { bridgeId });
  }

  /** Which stored row, if any, IS the machine this approval came from (audit
   *  2026-11-03 B-9). This used to be `findBridgeByInstance(resp.instance_name)`:
   *  a label the paired HOST reports about itself, so pairing a second computer
   *  that shares (or claims) a trusted row's name rewrote that row's address and
   *  key, and the working bridge was gone. `peers[]` lost that join for the same
   *  reason (security audit F2, below); the main approval kept it.
   *
   *  "Same machine" now needs proof, in this order:
   *    1. the row the operator re-paired FROM (they chose it);
   *    2. a row at the very same address;
   *    3. the NEW key tried at the stored row's own address: the daemon that
   *       issued it accepts it there (same machine, reached another way), a 401
   *       means a different daemon answers there (not this row);
   *    4. that address not answering at all: a matching node_id (or a row that
   *       never learned one) means the machine moved address.
   *  A node_id alone is NOT proof: it is in every mDNS advert, so step 3 runs
   *  before it - an impostor claiming the trusted row's node_id is still refused
   *  while the trusted machine answers. The one residual is an impostor claiming
   *  name AND node_id while the real machine is offline; nothing unauthenticated
   *  can tell those apart. `pairing_never_takes_over_a_trusted_row` proves each
   *  branch. Never throws: a failed probe is an "unknown", never a lost pairing. */
  async function provenSameRow(
    row: RowView, resp: PairingRequestStatusResponse, client: OpbridgeHttpClient,
  ): Promise<{ cfg: BridgeConfig; keepUrl: boolean } | null> {
    const key = resp.local_bearer;
    if (!key) return null;
    if (row.bridge_id) {
      const chosen = bridges().find((b) => b.id === row.bridge_id);
      if (chosen) return { cfg: chosen, keepUrl: false };
    }
    let nodeId = row.node_id ?? '';
    try {
      const h = await client.health();
      nodeId = (h as { node_id?: string }).node_id || nodeId;
    } catch { /* keep what the row knew */ }
    const instance = resp.instance_name ?? row.instance_name;
    const candidates: BridgeConfig[] = [];
    for (const c of [nodeId ? findBridgeByNode(nodeId) : null, instance ? findBridgeByInstance(instance) : null]) {
      if (c && !candidates.some((x) => x.id === c.id)) candidates.push(c);
    }
    const norm = (u: string) => u.trim().replace(/\/+$/, '').toLowerCase();
    for (const c of candidates) {
      if (norm(c.baseUrl) === norm(row.bridge_url)) return { cfg: c, keepUrl: false };
      let verdict: 'same' | 'other' | 'unknown' = 'unknown';
      try {
        await new OpbridgeHttpClient(c.baseUrl, key, 3000).getInheritances(Date.now() * 1000);
        verdict = 'same';
      } catch (err) {
        if (err instanceof OpbridgeError && err.kind === 'unauthorized') verdict = 'other';
        // A valid key without this route's scope (OPBridge 1.15.250+): still that daemon's key.
        else if (err instanceof OpbridgeError && err.httpStatus === 403) verdict = 'same';
      }
      if (verdict === 'same') return { cfg: c, keepUrl: true };
      if (verdict === 'other') {
        console.warn(`[pairing] "${instance}" at ${row.bridge_url} is not the stored row at ${c.baseUrl} (that computer refuses the new key): pairing it as its own row`);
        continue;
      }
      if (!c.nodeId || (nodeId && c.nodeId === nodeId)) return { cfg: c, keepUrl: false };
    }
    return null;
  }

  function applyApproval(
    row: RowView, resp: PairingRequestStatusResponse,
    same: { cfg: BridgeConfig; keepUrl: boolean } | null,
  ): void {
    // 2026-08-14 (supersedes DEC-063): pairing and "which bridge is
    // default" are separate concerns now. A re-pair (existing instance)
    // only refreshes the bearer — it does NOT promote, so re-pairing to
    // recover from a revoked/expired token never silently steals default
    // status from wherever the operator already set it. A genuinely NEW
    // bridge joins via addBridge()'s own default rule (first pair ever
    // in an empty store becomes default automatically; every later one
    // joins as an equal, non-default row) — no explicit override needed.
    batch(() => {
      if (resp.local_bearer) {
        const instance = resp.instance_name ?? row.instance_name;
        // Proven the same machine (provenSameRow, B-9), never matched by name.
        const existing = same ? bridges().find((b) => b.id === same.cfg.id) ?? null : null;
        if (existing && same) {
          // Refresh bearer in place instead of appending a duplicate that
          // would orphan a stale-bearer row (with a live /health poller
          // and a phantom badge dot). An address the new key was just proven
          // on is kept: it may be the suite hostname that a LAN-IP scan
          // cannot stand in for from the hosted origin.
          updateBridge(existing.id, {
            name: row.display_name || instance || row.bridge_url,
            baseUrl: same.keepUrl ? existing.baseUrl : row.bridge_url,
            bearer: resp.local_bearer,
            bridge_instance_name: instance,
            scope_bundle_id: resp.bundle_id,
            scope_bundle_version: resp.bundle_version,
          });
          armPairedRowFromScan(existing.id, row.bridge_url);
        } else {
          const created = addBridge({
            name: row.display_name || row.instance_name || row.bridge_url,
            baseUrl: row.bridge_url,
            bearer: resp.local_bearer,
            bridge_instance_name: instance,
            scope_bundle_id: resp.bundle_id,
            scope_bundle_version: resp.bundle_version,
          });
          armPairedRowFromScan(created.id, row.bridge_url);
        }
      }
      // ─── The peer fan-out is ADD-ONLY (2026-09-02, security audit F2) ─────
      //
      // This loop used to `updateBridge` an existing row when `peer.instance_name`
      // matched it, rewriting that row's `baseUrl` AND `bearer`. `resp` is the
      // REMOTE HOST'S OWN JSON, so that let any host we pair with re-point an
      // already-trusted bridge at itself by claiming a colliding name.
      //
      // MEASURED, not reasoned about: `_probe_bridge_param_pairs_any_host.ts`
      // run C seeded a real `is_primary` row and paired with a hostile host
      // whose peers[] claimed that row's instance_name. The row's baseUrl was
      // rewritten to the attacker's address and its bearer replaced. No empty
      // store needed.
      //
      // Joining on `instance_name` was always the wrong key and this repo says
      // so - `httpClient.ts`'s `PairedBridgeEntry.node_id` doc comment ends
      // "Do NOT join on `instance_name` (unstable mDNS label)". The strong key
      // simply is not available here: `PairingPeerRow` (httpClient.ts:605-611)
      // carries no node_id at all, so this call site CANNOT verify the claim.
      // When you cannot verify, do not overwrite.
      //
      // Why dropping the refresh is safe, checked rather than assumed:
      // `opbridge/inheritanceDrainer.ts` already refreshes a dormant peer's
      // bearer, and it matches on `r.nodeId === inh.node_id` - the strong key.
      // The one residual is a peer row whose nodeId is still null (never had a
      // successful /health), which the drainer cannot match. That closes
      // itself: a peer we cannot reach has no use for a fresh bearer, and the
      // moment it answers /health again `reportBridgeIdentity` backfills the
      // nodeId and the drainer takes over.
      //
      // Adding an UNKNOWN peer stays as it was - that is the fan-out feature,
      // and the fact that the whole response is untrusted is F1's problem
      // (the confirmation gate), not this loop's.
      for (const peer of resp.peers) {
        if (peer.status !== 'ok' || !peer.bearer) continue;
        const existingPeer = peer.instance_name ? findBridgeByInstance(peer.instance_name) : null;
        if (existingPeer) {
          // Known name: leave the row exactly as it is. Never take a URL or a
          // bearer for a bridge we already trust from a peer list.
          console.warn(
            `[pairing] ignoring peer entry for already-known bridge "${peer.instance_name}" - ` +
            'a peer list may not rewrite an existing row (security audit F2)',
          );
        } else {
          addBridge({
            name: peer.instance_name,
            baseUrl: peer.bridge_url,
            bearer: peer.bearer,
            bridge_instance_name: peer.instance_name,
            scope_bundle_id: resp.bundle_id,
            scope_bundle_version: resp.bundle_version,
            is_primary: false,
          });
        }
      }
    });
  }

  /** Set a paired row as the default bridge (2026-08-14, supersedes DEC-465's
   *  change-primary "switch") — a plain per-row action now, not a mode. No
   *  re-pair: the row already holds a live connection + bearer. */
  function setAsDefault(row: RowView): void {
    if (!row.bridge_id) return;
    setPrimary(row.bridge_id);
  }

  /** Per-bridge MIDI output override. `defaultMidiOutput` has always been
   *  the field every consumer reads (dispatch, ruleSync, timeline/bridgeSync,
   *  panic, pre-flight) — `DEFAULT_MIDI_PORT` only ever
   *  filled nulls. What was missing was any way to SET it: a grep for this
   *  field across `.tsx` returned nothing before this, so a mesh whose
   *  machines name their ports differently had no answer at all.
   *
   *  Writes through `updateBridge` so it lands in the same persisted row as
   *  baseUrl/bearer and survives a reload; a null clears back to the
   *  constant rather than storing an empty string. */
  function setMidiOutput(row: RowView, port: string | null): void {
    if (!row.bridge_id) return;
    updateBridge(row.bridge_id, { defaultMidiOutput: port });
  }

  /** Lists THIS row's own bridge's ports, never the default bridge's.
   *  Querying the wrong bridge returns another machine's port list and is
   *  the exact trap ConnectedDevices already documents for pedal detect —
   *  and it is worse here, because the two machines' lists genuinely
   *  disagree, so a wrong answer looks plausible. */
  async function fetchPortsFor(row: RowView): Promise<string[]> {
    if (!row.bridge_id) throw new Error('Not paired');
    const conn = getConnection(row.bridge_id);
    if (!conn || conn.status() !== 'connected') {
      throw new Error('Not connected - cannot list this bridge’s MIDI ports.');
    }
    const { outputs } = await conn.httpClient().midiPorts();
    return outputs;
  }

  function cancelPair(row: RowView): void {
    const p = pendingByKey.get(row.key);
    if (!p) return;
    p.controller.abort();
    pendingByKey.delete(row.key);
    setPendingTick((n) => n + 1);
    if (approvalPromptRow()?.key === row.key) closeApprovalPrompt();
  }

  function tryAgain(row: RowView): void {
    setTransientState((m) => {
      const next = new Map(m);
      next.delete(row.key);
      return next;
    });
    void startPair(row);
  }

  function disconnectRow(row: RowView): void {
    if (!row.bridge_id) return;
    removeBridge(row.bridge_id);
  }

  function markTransient(key: string, state: RowState, durationMs: number): void {
    setTransientState((m) => {
      const next = new Map(m);
      next.set(key, state);
      return next;
    });
    setTimeout(() => {
      setTransientState((m) => {
        const next = new Map(m);
        if (next.get(key) === state) next.delete(key);
        return next;
      });
    }, durationMs);
  }

  /** Accept EITHER a bare bridge URL or a full pairing link. The OPBridge
   *  dashboard's "Pair a new device" panel hands out the latter
   *  (`https://opbuttons.…/connect?bridge=<encoded>`), and its Copy button
   *  puts exactly that on the clipboard — so on an installed app, where the
   *  QR can't be used at all (see isInstalledApp), pasting that link is the
   *  shortest route in. Without this, pasting the copied link failed the
   *  `^https?://` check's INTENT: it is a valid URL, just not the bridge's.
   *  2026-08-30. */
  // Implementation moved to util/pairingLink.ts so the manual field, the
  // ?bridge= flow and the in-app QR scanner all accept identical inputs.

  /** Read a pairing link (or bridge URL) off the clipboard and submit it.
   *  Never silently no-ops: clipboard read can be denied or unsupported, and
   *  a dead button is exactly the failure this session was spent chasing. */
  async function pasteIntoManualUrl(): Promise<void> {
    setManualError(null);
    try {
      const text = (await navigator.clipboard?.readText())?.trim();
      if (!text) {
        setManualError('Clipboard is empty - copy the pairing link on the computer first.');
        return;
      }
      setManualUrl(text);
      await submitManualUrl({ confirmedByUser: true }); // the operator tapped Paste
    } catch {
      setManualError('Could not read the clipboard. Paste into the box above instead, then tap Connect.');
    }
  }

  /**
   * Probe an address and start pairing with it.
   *
   * ─── SECURITY GATE (security audit F1, 2026-09-02) ────────────────────
   *
   * `confirmedByUser` is REQUIRED and has no default, deliberately. Every new
   * call site has to state whether a human actually asked for this, and a
   * caller that says `false` gets pre-filled-and-prompted rather than paired.
   * A future entry point added without thinking therefore FAILS CLOSED.
   *
   * WHY. `?bridge=<url>` used to land here and auto-submit with no gesture in
   * between: `App.tsx`'s boot handler opens Connect whenever the param is
   * present, this component's `onMount` called `tryConnectFromUrlParam`, and
   * that called straight through to here. The only validation was
   * `/^https?:\/\/.+/`, and a host was accepted as an OPBridge purely on its
   * own `{ok, app:'OPBridge', instance_name}` JSON - all attacker-controlled.
   * So opening one crafted link stored whatever bearer that host returned, as
   * `is_primary` on an empty store, which 16 call sites resolve as "the
   * bridge" via `all.find(is_primary) ?? all[0]`.
   *
   * MEASURED, not reasoned about: `_probe_bridge_param_pairs_any_host.ts` run
   * A, against a real hostile HTTP server over a real cross-origin fetch. The
   * app then fired `/pairing/inheritances`, `/fixtures` and
   * `POST /rules/opbuttons` at that host within seconds.
   *
   * WHY A PRE-FILL AND NOT A MODAL. The whole point of the link is that
   * typing a 16-hex-char subdomain on an iPad is real friction, and a modal
   * would be a second thing to dismiss. Filling the field the operator would
   * have typed into and letting them tap the Connect button that is already
   * there costs one tap, adds no new UI, and tells them WHICH address arrived
   * - which silent pairing never did. A scan, a paste and the button are all
   * deliberate acts and pass `true`; only a URL parameter is not.
   */
  async function submitManualUrl(opts: { confirmedByUser: boolean }): Promise<void> {
    if (!opts.confirmedByUser) {
      // Show the operator what arrived and let them decide. Deliberately does
      // NOT probe: a probe is a request to an unvalidated host, and while
      // /health leaks little it is still traffic a link alone should not cause.
      setManualOpen(true);
      setManualError(null);
      setScanNotice('Bridge address from a link. Check it, then tap Connect.');
      return;
    }
    const raw = expandBridgeCode(extractBridgeUrl(manualUrl().trim()));
    setManualError(null);
    setScanNotice(null);
    if (!/^https?:\/\/.+/.test(raw)) {
      setManualError("Type the computer's 16-character code, or an address starting with https://");
      return;
    }
    const baseUrl = raw.replace(/\/+$/, '');
    // Probe /health first — if the bridge answers, treat it exactly as
    // if the scan had found it (add to discovered list + start pair).
    // Rides WebTransport when a QR supplied the advert, so this probe works
    // at a venue where `baseUrl` cannot resolve at all.
    const client = pairingClient(baseUrl, 3000);
    try {
      const h = await client.health();
      if (!h.ok || h.app !== 'OPBridge' || !h.instance_name) {
        setManualError('That address answered, but does not look like an OPBridge.');
        return;
      }
      const row: DiscoveredBridge = {
        instance_name: h.instance_name,
        bridge_url: baseUrl,
        display_name: h.machine_name ?? h.instance_name,
        owner: h.owner ?? null,
        source: 'lan-scan',
        reachable: true,
      };
      setDiscovered((prev) => mergeDiscovered(prev, [row]));
      setManualOpen(false);
      void startPair({
        key: h.instance_name,
        instance_name: h.instance_name,
        display_name: row.display_name,
        owner: row.owner,
        bridge_url: baseUrl,
        state: 'available',
        paired: false,
        bridge_id: null,
        is_primary: false,
        is_peer: false,
        midi_output: null,
      });
    } catch (_err) {
      setManualError("Can't reach that address. Check the address and that OPBridge is running.");
    }
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <section class="settings-section" data-section="connect">
      <header class="settings-section-head">
        <Show when={primaryBridge() !== null} fallback={
          <p class="settings-section-sub">
            OPButtons connects directly to every OPBridge on your network. Pick the machine running Ableton first to keep latency low; it becomes your default, and everything you pair after this stays equally reachable.
          </p>
        }>
          <p class="settings-section-sub">
            Every paired OPBridge below is directly and equally reachable. The default is just what a new item sends to until you point it elsewhere.
          </p>
        </Show>
      </header>

      {/* Bearer-revoked banner (Phase B). Scoped to the DEFAULT row — the
          consumer WS (consumerWs.ts) is still a single socket bound to
          whichever bridge is default, so revocation is only visible for
          that one row (/health itself is unauthenticated, so a revoked
          bearer still reads "connected" there). Re-pair reuses the
          normal pairing flow. */}
      <Show when={consumerRevoked() && assembleRows().paired.find((r) => r.is_primary)}>
        {(primary) => (
          <div class="connect-banner connect-banner-alert" role="alert">
            <div class="connect-banner-body">
              <div class="connect-banner-title">Access revoked</div>
              <div class="connect-banner-sub">
                OPBridge no longer accepts this device's key. Re-pair to reconnect.
              </div>
            </div>
            <button
              type="button"
              class="settings-primary"
              onClick={() => { void startPair(primary()); }}
            >
              Re-pair
            </button>
          </div>
        )}
      </Show>

      {/* Scope-denied banner (DEC-568 `scope_not_granted`). SEPARATE from the
          revoked banner directly above, because the remedies are opposites and
          conflating them is what made this state unreadable: a revoked bearer
          heals itself via a credential refresh, whereas this one never can -
          the grant was never issued, so no amount of refreshing or waiting
          will add it. That is also why `onDenied` starts no re-probe: this is
          the one auth state where retrying is provably pointless, and showing
          a "reconnecting" spinner for it would be a lie that never resolves.
          Wording says what the operator must actually DO. */}
      <Show when={consumerDenied() && assembleRows().paired.find((r) => r.is_primary)}>
        {(primary) => (
          <div class="connect-banner connect-banner-alert" role="alert">
            <div class="connect-banner-body">
              <div class="connect-banner-title">Missing permission</div>
              <div class="connect-banner-sub">
                This device's key is valid, but OPBridge did not grant it everything
                OPButtons needs. Re-pairing is the only fix - waiting will not help.
              </div>
            </div>
            <button
              type="button"
              class="settings-primary"
              onClick={() => { void startPair(primary()); }}
            >
              Re-pair
            </button>
          </div>
        )}
      </Show>

      {/* New capabilities available (Phase B silent-push). A peer bridge minted
          a bearer for this device; informational  -  it'll appear as a
          paired row automatically, no action needed. */}
      <Show when={!consumerRevoked() && availableInheritances().length > 0}>
        <div class="connect-banner connect-banner-info">
          <div class="connect-banner-body">
            {/* SAYS WHAT IS TRUE, not what used to be planned. This read
                "they will appear below shortly" - a promise kept only by the
                drainer's auto-ADD half, which has been off since 2026-08-30
                and stays off (App.tsx wires `autoAdd: false`). So bridges the
                operator has not paired never appeared, and the banner sat
                there indefinitely claiming they would.

                What IS true: the mesh has offered credentials for these, so
                pairing them costs one scan rather than a full ceremony, and
                if one is already in the list below its bearer self-heals. */}
            <div class="connect-banner-sub">
              {availableInheritances().length === 1
                ? '1 other OPBridge on your mesh has credentials ready  -  pair it below and it connects without a full ceremony.'
                : `${availableInheritances().length} other OPBridges on your mesh have credentials ready  -  pair them below and they connect without a full ceremony.`}
            </div>
          </div>
        </div>
      </Show>

      <Show when={scanRunning() && !scanCompleted()}>
        <div class="connect-empty">
          <div class="connect-spinner" aria-hidden="true" />
          <div class="connect-empty-title">Looking for OPBridges…</div>
          <div class="connect-empty-sub">This takes a couple of seconds.</div>
        </div>
      </Show>

      <Show when={scanCompleted() && scanFoundCount() === 0 && bridges().length === 0}>
        <div class="connect-empty">
          <div class="connect-empty-title">No OPBridges found on this network.</div>
          <div class="connect-empty-sub">
            Is OPBridge running on your Mac or Windows machine? Check that both devices are on the same Wi-Fi.
          </div>
          {/* 2026-08-13 — the guided self-signed cert-install card (iOS
              profile-install + Certificate Trust Settings toggle) is
              RETIRED from the UI on operator instruction: "user installing
              the cert is not an option anymore." Confirmed against a real
              iPad the same night — even when reachable, that flow is a
              genuine two-manual-step dance no website can automate around
              (Apple's own security posture), and it turned out to be
              actively broken on the operator's network besides (blank
              page on the download link — never root-caused, likely mDNS/
              router client-isolation, orthogonal to the cert steps
              themselves either way).
              The self-signed opbridge.local:8768 listener ITSELF is
              untouched and still tried automatically, silently, as part
              of suiteHostedCandidates() — DEC-526's offline-safe fallback
              stays alive server-side, forever, unconditionally. Only the
              user-facing "here are 4 manual steps" card is gone. The real
              answer for a device that can't auto-connect is now a pairing
              link/QR from whoever runs OPBridge (the ?bridge= flow above),
              not a DIY cert dance — that ask is filed to OPBridge
              (opbridge-opbuttons.md) so the dashboard can generate one. */}
          <Show when={isOnSuiteDomain()}>
            <div class="connect-empty-sub" style="margin-top:8px">
              If your browser just asked for local network access, allow it — Chrome and Edge require a one-time permission before this page can reach OPBridge on your machine.
            </div>
          </Show>
          <div class="connect-empty-sub" style="margin-top:8px">
            Still nothing? On the computer running OPBridge, open its dashboard and click <strong>"Pair a new device"</strong> in the Pairing panel - scan the QR (or tap the link) from this device and you're connected, no typing needed.
          </div>
          {/* 2026-08-30 (operator, real iPad): scanning the QR from an
              INSTALLED (home-screen) app is a dead end on iOS - the camera
              hands the link to Safari, and an installed web app has its own
              storage, so Safari gets paired and the installed app stays
              empty. The QR advice above is only actionable in a browser tab.
              Say so, and point at the field that does work here. */}
          <Show when={isInstalledApp()}>
            <div class="connect-empty-sub" style="margin-top:8px">
              <strong>You're in the installed app.</strong> Scanning the QR here won't work - iOS opens it in Safari, which pairs Safari instead of this app. Copy the pairing link from that same dashboard panel and paste it below, or type the address in by hand. You only have to do it once.
            </div>
          </Show>
          <div class="connect-empty-actions">
            {/* THERE IS EXACTLY ONE "Connect via QR code" BUTTON, and it is
                NOT here - it is the full-width one below, outside the empty
                state, which renders in every state.

                A second copy used to live here. Both were gated
                independently (this one on "scan found nothing and nothing is
                paired", the other on `!manualOpen()`), so in the empty state
                - the first thing a new device ever sees - they rendered
                TOGETHER and the page offered the same action twice. Operator
                2026-09-07: "it has 2 connect via qr code buttons, i thought
                we decided to use 1, the fanout to mesh connect."

                Deleted rather than re-gated: the surviving button already
                covers this state, and one QR route is the whole point - it
                is the zero-typing path that fans out to the entire mesh from
                a single ceremony. `connect_page_one_qr_cta` pins the count. */}
            <button
              type="button"
              class="settings-secondary"
              onClick={() => { void runScan(); }}
              disabled={scanRunning()}
            >
              {scanRunning() ? 'Scanning…' : 'Scan again'}
            </button>
          </div>
        </div>
      </Show>

      {/* Paired bridges — flat, equal list (2026-08-14, supersedes the
          primary-hero + dim-peer treatment). Every row here already holds
          its own live, directly-reachable connection. */}
      <div class="connect-list" data-row-version={rowVersion()}>
        <Show when={assembleRows().paired.length > 0}>
          <For each={assembleRows().paired}>
            {(row) => (
              <ConnectRow
                row={row}
                onConnect={() => { /* already paired */ }}
                onCancel={() => cancelPair(row)}
                onTryAgain={() => tryAgain(row)}
                onDisconnect={() => disconnectRow(row)}
                onSetDefault={() => setAsDefault(row)}
                onSetMidiOutput={(port) => setMidiOutput(row, port)}
                onListPorts={() => fetchPortsFor(row)}
              />
            )}
          </For>
        </Show>

        {/* Candidates — LAN-scan discovered, not yet paired. */}
        <Show when={assembleRows().candidates.length > 0}>
          <div class="connect-subhead">
            {assembleRows().paired.length === 0 ? 'Choose your default OPBridge' : 'Other machines on your network'}
          </div>
          <For each={assembleRows().candidates}>
            {(row) => <ConnectRow
              row={row}
              onConnect={() => void startPair(row)}
              onCancel={() => cancelPair(row)}
              onTryAgain={() => tryAgain(row)}
              onDisconnect={() => disconnectRow(row)}
            />}
          </For>
        </Show>

        {/* Peers — seen on the mesh, not yet paired by this device.
            2026-08-30: these used to be informational-only with
            `onConnect` wired to a NO-OP, while a declined peer was still
            rendered WITH a Connect button (see ConnectRow) — so the one
            row that most needed an action had a button that did literally
            nothing when tapped. That is the "tap Connect and nothing
            happens" the operator reported. Now wired to the real
            startPair() so it either pairs or reports why it couldn't
            (DEC-186: Connect must not lie). */}
        <Show when={assembleRows().peers.length > 0}>
          <div class="connect-subhead">Seen on your network</div>
          <For each={assembleRows().peers}>
            {(row) => <ConnectRow
              row={row}
              onConnect={() => void startPair(row)}
              onCancel={() => cancelPair(row)}
              onTryAgain={() => tryAgain(row)}
              onDisconnect={() => {/* not paired — nothing to disconnect */}}
            />}
          </For>
        </Show>
      </div>

      <Show when={!manualOpen()}>
        {/* PROMOTED OUT OF THE TEXT-LINK ROW, operator 2026-09-07. This was a
            small underlined link sharing a line with the manual-entry
            disclosure, separated by a middot - which made the zero-typing
            route look like the same weight as a troubleshooting link. On the
            iPad it is the PRIMARY way to pair: the dashboard shows a QR, this
            scans it, nothing is typed. It is now a full-width button above the
            row rather than a peer of it.

            The disclosure stays a text link and its text stays VERBATIM -
            five smokes locate it by that exact string. */}
        <div class="connect-qr-cta-wrap">
          <button
            type="button"
            class="connect-qr-cta"
            onClick={() => setScanOpen(true)}
          >
            <IconQr />
            <span>Connect via QR code</span>
          </button>
        </div>
        {/* A scan does not open the manual panel, so its notice ("Offline
            path armed", a refused offline address) must show here too: it
            used to render only inside that panel, where no one saw it. */}
        <Show when={scanNotice()}>
          <div class="connect-scan-notice">{scanNotice()}</div>
        </Show>
        <div class="connect-manual-toggle">
          <button
            type="button"
            class="connect-manual-link"
            onClick={() => setManualOpen(true)}
          >
            Not seeing your OPBridge?
          </button>
        </div>
      </Show>

      <Show when={manualOpen()}>
        {/* A form so Return on the iPad keyboard connects: the keyboard
            covers the Connect button (operator 2026-10-18). */}
        <form
          class="connect-manual"
          onSubmit={(e) => { e.preventDefault(); void submitManualUrl({ confirmedByUser: true }); }}
        >
          <label class="connect-manual-label">
            <span>Type the computer's code (or its address)</span>
            {/* 2026-08-30: the placeholder used to read
                `http://192.168.50.51:8765` unconditionally - a URL that
                CANNOT work from the hosted HTTPS origin (mixed content
                blocks a plain-http fetch to another device), so on the one
                surface where people actually need this field it suggested
                the one shape guaranteed to fail. On the suite domain, show
                the publicly-trusted per-bridge hostname shape instead - the
                one the QR encodes and the only one that works there. */}
            <input
              type="text"
              class="connect-manual-input"
              value={manualUrl()}
              onInput={(e) => setManualUrl(e.currentTarget.value)}
              // The iPad keyboard opens over the bottom half of the screen
              // and iOS cannot scroll a field inside Navigate's fixed layer
              // clear of it: move the panel to the top of the section once
              // the keyboard has risen.
              onFocus={(e) => {
                const panel = e.currentTarget.closest('.connect-manual');
                setTimeout(() => panel?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 350);
              }}
              autocapitalize="off"
              autocorrect="off"
              autocomplete="off"
              spellcheck={false}
              enterkeyhint="go"
              placeholder={isOnSuiteDomain()
                ? 'e.g. 8cfcea73b7e12458'
                : 'http://192.168.50.51:8765'}
            />
          </label>
          <div class="connect-manual-actions">
            <button
              type="submit"
              class="settings-primary"
            >
              Connect
            </button>
            {/* One-tap route for the installed app: copy the pairing link on
                the computer, get it to this device however (AirDrop, message),
                paste. Reading the clipboard needs a user gesture + permission,
                and Safari can refuse — so a failure just tells you to paste
                into the field by hand rather than silently doing nothing. */}
            <button
              type="button"
              class="settings-secondary"
              onClick={() => { void pasteIntoManualUrl(); }}
            >
              Paste link
            </button>
            <button
              type="button"
              class="settings-secondary"
              onClick={() => { setManualOpen(false); setManualError(null); }}
            >
              Cancel
            </button>
          </div>
          <Show when={scanNotice()}>
            <div class="connect-scan-notice">{scanNotice()}</div>
          </Show>
          <Show when={manualError()}>
            <div class="connect-manual-error">{manualError()}</div>
          </Show>
        </form>
        {/* Room below the panel so it CAN scroll to the top of the section
            above the keyboard (see the input's onFocus). */}
        <div class="connect-manual-keyboard-room" aria-hidden="true" />
      </Show>

      {/* In-app camera scanner. Feeds the SAME submitManualUrl path as a
          typed address or a pasted link, so there is exactly one pairing
          code path to reason about. The scanner has already stopped the
          camera before onFound runs (iOS drops a live stream on the
          history.replaceState that pairing performs). */}
      <Show when={doorMove()}>
        {(m) => {
          const host = (() => { try { return new URL(m().advert.url).hostname; } catch { return m().advert.url; } })();
          const names = m().rows.map((r) => r.name).join(', ');
          return (
            <ConfirmChoice
              title="Use a new offline address?"
              message={`This code moves ${names}'s offline connection to ${host}. Only do this if you scanned it off that computer's own screen.`}
              options={[
                { key: 'move', label: `Use ${host}`, hint: 'The computer moved, for example to a new venue network' },
                { key: 'keep', label: 'Keep the current address', hint: 'Connect without the code\'s offline address', tone: 'neutral' },
              ]}
              onChoose={(key) => {
                const pending = m();
                setDoorMove(null);
                if (key === 'move') continueScan(pending.bridgeUrl, pending.advert, pending.rows.map((r) => r.id));
                else continueScan(pending.bridgeUrl, null, []);
              }}
              onCancel={() => setDoorMove(null)}
            />
          );
        }}
      </Show>

      <Show when={scanOpen()}>
        <QrScanner
          onFound={(bridgeUrl, raw) => {
            setScanOpen(false);
            setManualError(null);
            // Arm the offline path FIRST, before any network call. This is the
            // break-glass case: a device whose cached pin died while it was
            // away, now at a venue with no internet. `submitManualUrl` below
            // probes /health, which cannot succeed there - so if the advert
            // were applied after it, the rescue would never run. Applying it
            // first also means the probe itself can ride WebTransport.
            const advert = extractWtAdvert(raw);
            // Refused by `extractWtAdvert`: a malformed pin, or a door that is
            // not a LAN address. Said, so the operator knows this code cannot
            // rescue the offline path.
            const refused = advert ? null : wtRefusal(raw);
            const moves = advert ? scannedAdvertNewDoors(advert, bridgeUrl) : [];
            if (advert && moves.length > 0) {
              setDoorMove({ advert, bridgeUrl, rows: moves });
              return;
            }
            continueScan(bridgeUrl, advert, [], refused);
          }}
          onClose={() => setScanOpen(false)}
        />
      </Show>

      <Show when={bridges().length > 0 || discovered().length > 0}>
        <div class="connect-rescan">
          <button
            type="button"
            class="settings-secondary"
            onClick={() => { void runScan(); }}
            disabled={scanRunning()}
          >
            {scanRunning() ? 'Scanning…' : 'Rescan network'}
          </button>
        </div>
      </Show>

      <Show when={approvalPromptRow()}>
        {(row) => {
          // Read live off pendingByKey each tick rather than the row's own
          // (possibly stale) countdown_remaining_ms - `pendingTick()` is the
          // reactive trigger, same 1Hz ticker the inline row pill already
          // depends on, so both stay in lockstep off one clock.
          const pending = () => { void pendingTick(); return pendingByKey.get(row().key); };
          return (
            <ConnectApprovalModal
              displayName={row().display_name}
              outcome={approvalOutcome()}
              remainingMs={Math.max(0, (pending()?.expires_at_ms ?? Date.now()) - Date.now())}
              totalMs={pending()?.total_ms ?? 60_000}
              onCancel={() => cancelPair(row())}
              onTryAgain={() => { closeApprovalPrompt(); tryAgain(row()); }}
              onClose={() => closeApprovalPrompt()}
            />
          );
        }}
      </Show>
    </section>
  );
}

function ConnectRow(props: {
  row: RowView;
  onConnect: () => void;
  onCancel: () => void;
  onTryAgain: () => void;
  onDisconnect: () => void;
  /** Only meaningful on a paired, non-default row — sets it as the
   *  default bridge (2026-08-14, supersedes "Change primary"). Absent
   *  entirely for candidate/peer rows, which aren't paired yet. */
  onSetDefault?: () => void;
  /** Paired rows only — writes this bridge's `defaultMidiOutput`. `null`
   *  clears the override back to the built-in default. */
  onSetMidiOutput?: (port: string | null) => void;
  /** Paired rows only — live `GET /midi/ports` outputs for THIS bridge. */
  onListPorts?: () => Promise<string[]>;
}) {
  // Per-row, local to the row — the port list is a read of ONE bridge and
  // must never be shared across rows, since two machines' lists genuinely
  // differ and a leaked list would offer ports the target host doesn't have.
  const [ports, setPorts] = createSignal<string[]>([]);
  const [portsOpen, setPortsOpen] = createSignal(false);
  const [portsBusy, setPortsBusy] = createSignal(false);
  const [portsError, setPortsError] = createSignal('');
  /** True only once a list has actually come back. Gates the "port not on
   *  this host" warning so an empty `ports()` (never read, or a failed read)
   *  can't masquerade as "the port is missing" — the check must be able to
   *  distinguish "looked and it isn't there" from "never looked". */
  const [portsSeen, setPortsSeen] = createSignal(false);

  async function openPorts(): Promise<void> {
    if (!props.onListPorts) return;
    setPortsBusy(true);
    setPortsError('');
    try {
      const outs = await props.onListPorts();
      setPorts(outs);
      setPortsSeen(true);
      setPortsOpen(true);
    } catch (err) {
      setPortsError(err instanceof Error ? err.message : String(err));
    } finally {
      setPortsBusy(false);
    }
  }

  function stateLabel(): string {
    // Peer rows are mesh-transparency copy only — not yet paired by this
    // device, so there's no direct reachability signal to report.
    // Never promise auto-pairing to a peer we have declined - it is the one
    // peer that provably will NOT pair on its own.
    // 2026-08-30: "pairs automatically" was only ever true while the
    // inheritance drainer ran. With auto-pairing off (App.tsx) it promised
    // something that could never happen, on a row that also had no action -
    // a dead end. Both halves are now honest: tap Connect.
    if (props.row.is_peer) {
      return props.row.declined
        ? 'You disconnected this one - tap Connect to use it again'
        : 'Seen on network - tap Connect to pair';
    }
    switch (props.row.state) {
      case 'connected': return 'Connected';
      case 'available': return 'Available';
      case 'waiting': return 'Approve on OPBridge dashboard';
      case 'unreachable': return props.row.paired ? 'Offline' : 'Unreachable';
      case 'denied': return 'Denied';
      case 'expired': return 'Timed out';
    }
  }

  function formatCountdown(): string {
    if (!props.row.countdown_remaining_ms) return '';
    const s = Math.ceil(props.row.countdown_remaining_ms / 1000);
    return `${s}s`;
  }

  return (
    <div
      class="connect-row"
      data-state={props.row.state}
      data-primary={props.row.is_primary ? 'true' : undefined}
      data-peer={props.row.is_peer ? 'true' : undefined}
    >
      <div class="connect-row-dot" data-state={props.row.state} aria-hidden="true" />
      <div class="connect-row-body">
        <div class="connect-row-title">
          {props.row.display_name}
          <Show when={props.row.is_primary}>
            <span class="connect-row-default-badge">Default</span>
          </Show>
        </div>
        <div class="connect-row-sub">
          {stateLabel()}
          <Show when={props.row.state === 'waiting' && props.row.countdown_remaining_ms}>
            {' · '}
            <span class="connect-row-countdown">{formatCountdown()} remaining</span>
          </Show>
          <Show when={props.row.state !== 'waiting'}>
            {' · '}
            <span class="connect-row-url">{props.row.bridge_url}</span>
          </Show>
          <Show when={props.row.owner}>
            {' · '}
            <span class="connect-row-owner">{props.row.owner}</span>
          </Show>
        </div>
        {/* WHY THIS ROW DID NOT CONNECT ITSELF (2026-09-14). Until now these
            reasons existed only as `console.info`, which means they existed
            only on a machine with a console - and the device where mesh
            auto-adoption actually matters is the iPad, which has none. So the
            one reader who needed the diagnostic was the one reader who could
            never see it. Same argument as the black box readout in Settings,
            one surface down.

            Suppressed when `declined`, because `stateLabel()` already says
            that in the line above and two sentences saying the same thing
            reads as two separate problems. */}
        <Show when={props.row.adopt_reason && !props.row.declined}>
          <div class="connect-row-adopt-note">{props.row.adopt_reason}</div>
        </Show>
        <Show when={props.row.is_primary && props.row.state === 'connected'}>
          <div class="connect-row-clock" title="Clock source OPButtons reads from this OPBridge (GET /clock/status)">
            Clock: {clockSourceLabel(clockSource())}
          </div>
        </Show>
        <Show when={props.row.paired && !props.row.is_primary && props.onSetDefault}>
          <button type="button" class="connect-row-set-default" onClick={props.onSetDefault}>
            Set as default
          </button>
        </Show>
        <Show when={props.row.paired && props.onSetMidiOutput && props.onListPorts}>
          <div class="connect-row-midi">
            <span class="connect-row-midi-label">MIDI out</span>
            <Show
              when={portsOpen()}
              fallback={
                <>
                  <span class="connect-row-midi-port" data-unset={props.row.midi_output ? undefined : 'true'}>
                    {props.row.midi_output ?? 'not set'}
                  </span>
                  <button
                    type="button"
                    class="nv-b nv-b-sm connect-row-midi-btn"
                    disabled={props.row.state !== 'connected' || portsBusy()}
                    title={props.row.state !== 'connected'
                      ? 'Connect to this OPBridge to read the ports it can see'
                      : 'List this OPBridge’s MIDI output ports'}
                    onClick={() => { void openPorts(); }}
                  >
                    {portsBusy() ? 'Reading…' : 'Change'}
                  </button>
                </>
              }
            >
              <select
                class="connect-row-midi-select"
                value={props.row.midi_output ?? ''}
                onChange={(e) => {
                  props.onSetMidiOutput?.(e.currentTarget.value || null);
                  setPortsOpen(false);
                }}
              >
                <option value="">- use the built-in default -</option>
                <For each={ports()}>{(p) => <option value={p}>{p}</option>}</For>
                {/* A stored port the host can't currently see still has to be
                    selectable, or opening the picker on a bridge whose port
                    was unplugged would silently re-point the whole rig. */}
                <Show when={props.row.midi_output && !ports().includes(props.row.midi_output)}>
                  <option value={props.row.midi_output!}>{props.row.midi_output} (not on this host)</option>
                </Show>
              </select>
              <button type="button" class="nv-b nv-b-sm connect-row-midi-btn" onClick={() => setPortsOpen(false)}>Cancel</button>
            </Show>
          </div>
          {/* Surfaced, not swallowed: a stored port this host cannot see
              sends into nothing, and the failure is otherwise silent -
              /midi/send returns ok:false for an unknown port name. */}
          <Show when={!portsOpen() && portsSeen() && props.row.midi_output
                      && !ports().includes(props.row.midi_output!)}>
            <div class="connect-row-midi-warn">
              This OPBridge doesn’t have a port called “{props.row.midi_output}”. Nothing sent to it will arrive.
            </div>
          </Show>
          <Show when={portsError()}>
            <div class="connect-row-midi-warn">{portsError()}</div>
          </Show>
        </Show>
      </div>
      <div class="connect-row-actions">
        {/* Candidates get Connect; paired rows get Disconnect; peers get
            nothing (not yet paired). No more mode-gated "Make primary" -
            "Set as default" above is a plain per-row action now. */}
        {/* EVERY peer gets Connect (2026-08-30). Previously only a DECLINED
            peer did - and its handler was a no-op, so the single row that
            needed an action had a button that did nothing. With auto-pairing
            off, a non-declined peer has no other route either. */}
        <Show when={props.row.is_peer}>
          <button type="button" class="settings-primary" onClick={props.onConnect}>Connect</button>
        </Show>
        <Show when={!props.row.is_peer}>
          <Show when={props.row.state === 'available'}>
            <button type="button" class="settings-primary" onClick={props.onConnect}>Connect</button>
          </Show>
          <Show when={props.row.state === 'waiting'}>
            <button type="button" class="settings-secondary" onClick={props.onCancel}>Cancel</button>
          </Show>
          <Show when={props.row.state === 'denied' || props.row.state === 'expired'}>
            <button type="button" class="settings-primary" onClick={props.onTryAgain}>Try again</button>
          </Show>
          <Show when={props.row.state === 'connected' && props.row.paired}>
            <button type="button" class="settings-secondary" onClick={props.onDisconnect}>Disconnect</button>
          </Show>
          <Show when={props.row.state === 'unreachable' && props.row.paired}>
            <button type="button" class="settings-secondary" onClick={props.onDisconnect}>Remove</button>
          </Show>
        </Show>
      </div>
    </div>
  );
}

/** Why a scanned link's `wt=` door was not used, in the operator's words, or
 *  null when the link carried none (nothing to say). `extractWtAdvert` refuses
 *  a door for two reasons, and the notice names the one that applied. */
function wtRefusal(raw: string): string | null {
  let wt: string | null = null;
  try { wt = new URL(raw.trim()).searchParams.get('wt'); } catch { return null; }
  if (!wt) return null;
  let host = '';
  try { host = new URL(wt.trim()).hostname; } catch { /* not a URL: incomplete */ }
  return host && !isLanHostLiteral(host)
    ? 'This code\'s offline address was not used: it is not a local network address.'
    : 'This code\'s offline address was not used: the code is incomplete. Show it again on the computer and rescan.';
}
